// All marketing copy lives in this file so it can be edited without touching JSX.
// Phase 1 — Landing. Phase 9A — eleven V1 feature cards + four pricing tiers.

export const NAV = {
  brand: "Sema",
  links: [
    { label: "Product", href: "#product" },
    { label: "Why Sema", href: "#why" },
    { label: "Pricing", href: "#pricing" },
    { label: "Docs", href: "#docs", external: true },
  ],
  signIn: { label: "Sign in", href: "/login" },
  cta: { label: "Get started", href: "/signup" },
};

export const HERO = {
  eyebrow: "v1 · available now",
  title: "Ask your data anything. Trust the answer.",
  subtitle:
    "Sema is the semantic layer for enterprise AI. Connect 40+ databases, let Sema infer what the data means, and answer business questions in plain English — with audit-grade governance from day one.",
  primaryCta: { label: "Start free sandbox", href: "/signup" },
  secondaryCta: { label: "Book a 20-minute walkthrough", href: "/demo" },
};

export const LOGOS = {
  caption: "Built with our design partners across —",
  items: [
    "Senior living",
    "B2B SaaS",
    "Consumer lending",
    "Retail & e-commerce",
    "Manufacturing",
    "Logistics",
  ],
};

export const PROBLEM = {
  eyebrow: "the problem",
  title: "The fragmented stack is breaking analytics.",
  intro:
    "Every dashboard hides a maze of joins, business rules, and tribal knowledge. AI on top of that maze guesses — confidently and incorrectly.",
  pillars: [
    {
      stat: "4–6",
      label: "tools to answer one question",
      body: "Slack → BI → spreadsheet → SQL editor → docs → back to Slack. The round trip kills momentum and erodes trust.",
    },
    {
      stat: "1 in 3",
      label: "metrics drift across dashboards",
      body: "Revenue, churn, MAU — defined three different ways in three places. Nobody knows which is right anymore.",
    },
    {
      stat: "73%",
      label: "of AI answers cite the wrong column",
      body: "LLMs hallucinate against undocumented schemas. Without a semantic layer, retrieval grabs the closest-named field, not the right one.",
    },
  ],
};

export const BETS = {
  eyebrow: "three bets",
  title: "What we believe.",
  cards: [
    {
      n: "01",
      title: "The semantic graph is the control plane.",
      body: "Tables, columns, metrics, and business terms live as a typed graph — not a wiki, not a YAML file. Every query, dashboard, and agent reads from the same source of truth, and changes propagate.",
    },
    {
      n: "02",
      title: "Chat and canvas, unified.",
      body: "Conversation is the input; a versioned canvas of charts, queries, and explanations is the output. No more screenshotting numbers from one tool into another.",
    },
    {
      n: "03",
      title: "Governed by default.",
      body: "Row-level access, column masking, query logging, and signed answers are on at install. Turning governance off is a deliberate choice — not the consequence of forgetting it.",
    },
  ],
};

export const FEATURES = {
  eyebrow: "v1 features",
  title: "Eleven capabilities. One governed answer.",
  subtitle:
    "From source connection to audit-grade answer — every layer of the semantic stack ships today.",
  cards: [
    {
      icon: "Database",
      title: "Connectors",
      claim: "40+ sources, sandboxed sync.",
      bullets: [
        "Postgres, Snowflake, BigQuery, Databricks, Redshift, MS SQL, MySQL, MongoDB, S3 — plus more.",
        "Incremental sync with watermark + CDC where supported.",
        "Sandboxed execution — Sema never sees raw rows it shouldn't.",
      ],
    },
    {
      icon: "Table2",
      title: "Schema",
      claim: "Live introspection on connect.",
      bullets: [
        "Tables, columns, types, and keys profiled in seconds.",
        "Snapshots track drift over time.",
        "The substrate every other layer is built on.",
      ],
    },
    {
      icon: "Activity",
      title: "Table Health",
      claim: "Freshness, volume, nulls at a glance.",
      bullets: [
        "Row counts, freshness, and null ratios per table.",
        "Flags stale or empty tables before they break an answer.",
        "Health scores roll up to the workspace overview.",
      ],
    },
    {
      icon: "BookOpen",
      title: "Auto-Glossary",
      claim: "Plain-English definitions, generated.",
      bullets: [
        "Every table and column gets a business definition.",
        "Synonyms + sensitivity + lifecycle, human-confirmed.",
        "Embedded for semantic search across your vocabulary.",
      ],
    },
    {
      icon: "MessageSquare",
      title: "Chat + Auto-Viz",
      claim: "Plain English in. Auditable chart out.",
      bullets: [
        "Asks clarifying questions before guessing.",
        "Shows the SQL it ran and the graph nodes it traversed.",
        "Auto-selects the right chart for the answer.",
      ],
    },
    {
      icon: "FileText",
      title: "Reports",
      claim: "Save, schedule, and deliver.",
      bullets: [
        "Pin any answer as a saved report.",
        "Schedule recurring delivery by email.",
        "Versioned — diff today against last Tuesday.",
      ],
    },
    {
      icon: "BellRing",
      title: "Alerts",
      claim: "Know the moment a metric moves.",
      bullets: [
        "Threshold + deviation rules on any metric.",
        "Evaluated on a schedule, routed to owners.",
        "Every breach captured with frozen evidence.",
      ],
    },
    {
      icon: "Radio",
      title: "Signals",
      claim: "Drift becomes a governed record.",
      bullets: [
        "Reviewed alerts become signals with provenance.",
        "Approve → immutable registry entry.",
        "Prove what you knew, and when.",
      ],
    },
    {
      icon: "Network",
      title: "Semantic Graph",
      claim: "Verified joins, first-class metrics.",
      bullets: [
        "Navigate tables, joins, and metrics visually.",
        "Verify inferred joins once — every answer benefits.",
        "Metrics resolve by name across the org.",
      ],
    },
    {
      icon: "ShieldCheck",
      title: "RBAC",
      claim: "Role-appropriate access, enforced.",
      bullets: [
        "Viewer, analyst, admin, owner — enforced in code.",
        "Restricted columns refused or masked by role.",
        "Refusals happen at plan time, before SQL runs.",
      ],
    },
    {
      icon: "ScrollText",
      title: "Audit Log",
      claim: "Append-only, compliance-grade.",
      bullets: [
        "Every mutation recorded immutably.",
        "Filter by actor, kind, and date; export to CSV.",
        "The evidence pack for any decision, in one click.",
      ],
    },
  ],
};

export const ROADMAP = {
  eyebrow: "roadmap",
  title: "Where we're going.",
  cards: [
    {
      tag: "Available now",
      version: "V1",
      title: "Connect · Discover · Ask · Govern",
      body: "The full V1 surface — connectors, schema, health, glossary, chat + viz, reports, alerts, signals, graph, RBAC, and audit.",
      state: "now",
    },
    {
      tag: "Q1 2027",
      version: "V2",
      title: "Compliance + eval + federation",
      body: "A Compliance Agent for advanced governance, an AI eval harness, and federated joins across multiple warehouses.",
      state: "next",
    },
    {
      tag: "Q3 2027",
      version: "V3",
      title: "Predictive + embedded",
      body: "Auto-trained forecast models per metric. Embed Sema into your own product with a signed widget and provenance trail.",
      state: "later",
    },
  ],
};

export const ARCHITECTURE = {
  eyebrow: "architecture",
  title: "Built for enterprise, by default.",
  subtitle:
    "Sema runs in your VPC or ours. The semantic graph and the execution sandbox are separable — keep your data plane in your network.",
  pillars: [
    {
      title: "MCP Toolbox",
      body: "Every Sema capability is an MCP tool. Drop the toolbox into Claude, Cursor, or your in-house agent and inherit Sema's governance.",
    },
    {
      title: "OAuth2 / OIDC",
      body: "Bring your IdP — Okta, Entra, Auth0, Google Workspace. Group-mapped roles flow into row-level policies automatically.",
    },
    {
      title: "Sandboxed execution",
      body: "Query plans run in ephemeral containers with no outbound network. Generated SQL is statically analyzed before it ever sees a database.",
    },
    {
      title: "VPC deploy",
      body: "Helm chart for K8s. Bring your own Postgres + object store. Sema's control plane never holds your data — only the graph.",
    },
  ],
};

export const PRICING = {
  eyebrow: "pricing",
  title: "Simple, by seat.",
  subtitle:
    "Start free in a sandbox. Upgrade to a 14-day Pro trial — no card to start — and convert when your team is hooked.",
  toggle: { monthly: "Monthly", annual: "Annual −20%" },
  tiers: [
    {
      name: "Sandbox",
      priceMonthly: 0,
      priceAnnual: 0,
      unit: "free forever",
      tagline: "Evaluate Sema against your own data, free.",
      cta: { label: "Start free sandbox", href: "/signup" },
      featured: false,
      bullets: [
        "1 connector · 1 user.",
        "100 chat queries / month.",
        "5 saved reports · no scheduled delivery.",
        "No alerts or signals.",
        '"Powered by Sema" on embeds.',
      ],
    },
    {
      name: "Pro",
      priceMonthly: 49,
      priceAnnual: 39,
      unit: "/user/mo",
      seats: "min 3 seats · 14-day trial",
      tagline: "For teams running on governed answers.",
      cta: { label: "Start 14-day trial", href: "/signup" },
      featured: true,
      bullets: [
        "Unlimited connectors + chat.",
        "Unlimited reports, schedules, alerts, signals.",
        "RBAC + audit log.",
        "Email support.",
        "No card required to start the trial.",
      ],
    },
    {
      name: "Business",
      priceMonthly: 99,
      priceAnnual: 79,
      unit: "/user/mo",
      seats: "min 10 seats",
      tagline: "For orgs that need SSO and advanced governance.",
      cta: { label: "Start 14-day trial", href: "/signup" },
      featured: false,
      bullets: [
        "Everything in Pro.",
        "SSO + advanced governance (Compliance Agent — V2).",
        "AI eval harness + federated joins (V2).",
        "Priority support.",
        "99.9% SLA.",
      ],
    },
    {
      name: "Enterprise",
      price: "Contact",
      unit: "annual",
      tagline: "For regulated, multi-region, and self-hosted deployments.",
      cta: { label: "Talk to sales", href: "/demo" },
      featured: false,
      bullets: [
        "Everything in Business.",
        "SAML / SCIM · custom DPA · BYOK encryption.",
        "On-prem option (Helm).",
        "SOC 2 + HIPAA + GDPR evidence packs.",
        "Named CSM · 99.95% SLA.",
      ],
    },
  ],
};

export const FAQ = {
  eyebrow: "questions",
  title: "Things you'll want to know.",
  items: [
    {
      q: "Where does Sema actually store my data?",
      a: "Sema does not warehouse your data. The semantic graph — entities, relationships, metric definitions, embeddings — lives in Sema. When you ask a question, generated SQL runs against your source(s) directly, or in a sandboxed read replica you control. Choose VPC deploy and even the graph stays in your network.",
    },
    {
      q: "How do you actually prevent hallucination?",
      a: "Three layers. First, every query is grounded in the semantic graph — Sema only references entities, columns, and metrics you've approved. Second, generated SQL is statically analyzed (joins, filters, aggregation) before execution. Third, every answer ships with the SQL it ran, the graph nodes it touched, and any rows it sampled. If Sema can't ground an answer, it asks a clarifying question instead of guessing.",
    },
    {
      q: "What's in the free sandbox, and how does the trial work?",
      a: "The Sandbox is free forever: one connector, one user, 100 chat queries a month, and five saved reports. When you're ready, start a 14-day Pro trial — no credit card to begin. We prompt for a card on day 10; if you don't add one by day 14, you simply drop back to the Sandbox. No surprise charges.",
    },
    {
      q: "Can I self-host Sema?",
      a: "Yes — Enterprise tier ships a Helm chart for Kubernetes. Bring your own Postgres and object store. Sema's control plane runs entirely in your VPC and never phones home for data; metering happens via a signed monthly attestation.",
    },
    {
      q: "What does the pricing scale on?",
      a: "Seats. Pro is $49/user/month (min 3 seats); Business is $99/user/month (min 10 seats), and annual billing takes 20% off. We don't meter the number of queries, the size of your data, or the number of connectors on paid tiers — those would punish exactly the teams getting the most value.",
    },
  ],
};

export const FINAL_CTA = {
  eyebrow: "ready when you are",
  title: "Give your data a vocabulary.",
  subtitle:
    "Spin up a sandbox in two minutes against a sample warehouse. Or book a 20-minute walkthrough — bring your own questions.",
  primaryCta: { label: "Start free sandbox", href: "/signup" },
  secondaryCta: { label: "Book a walkthrough", href: "/demo" },
};

export const FOOTER = {
  tagline: "The semantic layer for enterprise AI.",
  columns: [
    {
      title: "Product",
      links: [
        { label: "Connectors", href: "/connectors" },
        { label: "Discovery", href: "/discovery" },
        { label: "Governance", href: "/governance" },
        { label: "Roadmap", href: "/roadmap" },
      ],
    },
    {
      title: "Company",
      links: [
        { label: "About", href: "/about" },
        { label: "Customers", href: "/customers" },
        { label: "Careers", href: "/careers" },
        { label: "Contact", href: "/contact" },
      ],
    },
    {
      title: "Resources",
      links: [
        { label: "Blog", href: "/blog" },
        { label: "Semantic layer guide", href: "/semantic-layer" },
        { label: "Docs", href: "/docs" },
        { label: "Changelog", href: "/changelog" },
        { label: "Trust", href: "/trust" },
        { label: "Media kit", href: "/#media-kit" },
      ],
    },
    {
      title: "Compare",
      links: [
        { label: "vs dbt Semantic Layer", href: "/compare/sema-vs-dbt-semantic-layer" },
        { label: "vs Cube", href: "/compare/sema-vs-cube" },
        { label: "vs AtScale", href: "/compare/sema-vs-atscale" },
        { label: "vs Looker", href: "/compare/sema-vs-looker" },
      ],
    },
    {
      title: "Legal",
      links: [
        { label: "Privacy Policy", href: "/privacy" },
        { label: "Terms & Conditions", href: "/terms" },
        { label: "DPDP Act (India)", href: "/dpdp" },
        { label: "Data Privacy", href: "/data-privacy" },
        { label: "EULA", href: "/eula" },
        { label: "DPA", href: "/dpa" },
        { label: "Subprocessors", href: "/subprocessors" },
      ],
    },
  ],
  compliance: "India DPDP Act 2023 aligned · SOC 2 program · GDPR-ready · HIPAA on request",
  copyright: "© 2026 Sema. All rights reserved.",
};

// ---------------------------------------------------------------------------
// Extended marketing sections (deep capability showcase, how-it-works, stats,
// security & compliance, testimonials, contact CTA, media kit).
// ---------------------------------------------------------------------------

export const EXTENDED = {
  eyebrow: "the trust layer",
  title: "Answers you can put in front of an auditor.",
  subtitle:
    "Governance isn't a feature bolted on the side — it's woven through every answer Sema returns. Here's the machinery that makes an answer defensible.",
  cards: [
    {
      icon: "ShieldCheck",
      title: "Signed Evidence Packs",
      claim: "Ed25519 signatures on every answer.",
      body: "Each answer is bundled with its question, SQL, lineage, and access context, then cryptographically signed. Re-verify the signature any time — tampering is mathematically detectable.",
      span: "lg",
      image: "/marketing/sema-screenshot-evidence.jpg",
    },
    {
      icon: "Share2",
      title: "Shareable audit links",
      claim: "Read-only, expiring, no login.",
      body: "Send an auditor a link that renders the signed pack — SQL, lineage, signature badge, PDF export — with zero access to the rest of your workspace. Every open is logged.",
      span: "sm",
    },
    {
      icon: "ClipboardCheck",
      title: "Governed checks",
      claim: "DPDP, BFSI & SaaS check sets.",
      body: "Curated, industry-specific compliance checks run on a schedule and freeze evidence the moment they fire — audit-readiness on autopilot.",
      span: "sm",
    },
    {
      icon: "Network",
      title: "Semantic graph + metrics",
      claim: "Verify a join once. Answer forever.",
      body: "Tables, columns, metrics, and business terms live as a typed graph. Confirm an inferred join once and every future answer inherits the correct path.",
      span: "sm",
    },
    {
      icon: "Radio",
      title: "Alerts → Signals registry",
      claim: "Drift becomes an immutable record.",
      body: "Threshold and deviation alerts on any metric. A reviewed breach becomes a signal with provenance in an append-only registry — prove what you knew, and when.",
      span: "sm",
    },
    {
      icon: "MessagesSquare",
      title: "Conversational query",
      claim: "Plain English in, auditable chart out.",
      body: "Ask in your own words. Sema asks clarifying questions before guessing, shows the SQL it ran and the nodes it touched, and picks the right chart automatically.",
      span: "lg",
      image: "/marketing/sema-screenshot-dashboard.jpg",
    },
  ],
};

export const HOW_IT_WORKS = {
  eyebrow: "how it works",
  title: "From warehouse to trusted answer in three moves.",
  subtitle: "No pipelines to rebuild, no metrics to re-model by hand. Sema meets your stack where it is.",
  steps: [
    {
      n: "01",
      title: "Connect",
      body: "Point Sema at your warehouse or upload a file. It introspects the schema, profiles every table, and maps the terrain in seconds — sandboxed and read-only.",
      icon: "PlugZap",
    },
    {
      n: "02",
      title: "Define",
      body: "Sema drafts plain-English definitions, infers joins, and proposes metrics. You confirm what's right; the semantic graph becomes your single source of truth.",
      icon: "BrainCircuit",
    },
    {
      n: "03",
      title: "Ask",
      body: "Anyone on the team asks in plain English and gets a governed, explainable answer — with the SQL, lineage, and a signed evidence pack attached.",
      icon: "Sparkles",
    },
  ],
};

export const STATS = {
  eyebrow: "by the numbers",
  title: "Built to be trusted at scale.",
  items: [
    { value: "40+", label: "database connectors" },
    { value: "100%", label: "answers with signed evidence" },
    { value: "<2s", label: "median time to a governed answer" },
    { value: "7yr", label: "audit retention, append-only" },
  ],
};

export const SECURITY = {
  eyebrow: "security & compliance",
  title: "Governed by default. Compliant by design.",
  subtitle:
    "Sema is built for regulated teams — aligned to India's DPDP Act 2023, GDPR-ready, and running a SOC 2 program. Your data plane can stay entirely inside your network.",
  pillars: [
    { icon: "Landmark", title: "India DPDP Act 2023", body: "Purpose limitation, consent records, data-principal rights workflows, and breach-notification readiness built into the platform." },
    { icon: "Lock", title: "Encryption everywhere", body: "TLS 1.2+ in transit, AES-256 at rest. Workspace LLM keys are Fernet-encrypted; secrets never touch the client." },
    { icon: "Users", title: "RBAC + column masking", body: "Viewer, analyst, admin, owner — enforced in code. Restricted columns are refused or masked at plan time, before any SQL runs." },
    { icon: "ScrollText", title: "Append-only audit log", body: "Every mutation and every answer recorded immutably, filterable by actor, kind, and date, exportable for your auditors." },
    { icon: "ServerCog", title: "VPC / self-host", body: "Deploy Sema's control plane in your own Kubernetes. Bring your own Postgres and object store — Sema never warehouses your rows." },
    { icon: "FileCheck2", title: "Evidence on demand", body: "SOC 2, HIPAA, and GDPR evidence packs generated from the live audit trail — not assembled by hand the week before a review." },
  ],
};

export const TESTIMONIALS = {
  eyebrow: "design partners",
  title: "Trusted by teams who can't afford a wrong number.",
  subtitle: "We build in the open with a small cohort of design partners across regulated industries.",
  quotes: [
    {
      quote: "Sema turned our 'which number is right?' Slack threads into a single governed answer with the SQL attached. Our audit prep went from weeks to an afternoon.",
      name: "Head of Data",
      org: "Senior-living operator",
    },
    {
      quote: "The signed evidence packs are the unlock. When compliance asks how we got a figure, we send a link — signature, lineage, and all.",
      name: "VP Analytics",
      org: "Consumer lending",
    },
    {
      quote: "We connected our warehouse on a Friday and the whole ops team was self-serving governed answers by Monday. No dashboards to rebuild.",
      name: "Director of BI",
      org: "B2B SaaS",
    },
  ],
};

export const CONTACT_SECTION = {
  eyebrow: "talk to us",
  title: "See Sema on your own data.",
  subtitle:
    "Tell us what you're trying to answer. We'll show you Sema running against a dataset like yours — and reply within one business day.",
  points: [
    "A 20-minute, no-slides walkthrough on data like yours.",
    "Straight answers on deployment, governance, and pricing.",
    "No pressure, no drip campaign — just a real conversation.",
  ],
};

export const TECHNOLOGY = {
  eyebrow: "the technology",
  title: "Not a chat-with-your-data toy. A governed analytics engine.",
  subtitle:
    "Anyone can wire an LLM to a database in an afternoon. What's hard — and what Sema does — is everything between the question and an answer you can defend: understanding meaning, planning safe SQL, visualizing it, and signing the result. Six layers, working as one.",
  beyond: {
    kicker: "beyond a chatbot",
    heading: "One question. A whole analyst's worth of work.",
    pillars: [
      {
        icon: "BarChart3",
        title: "It visualizes",
        body: "Sema picks the right chart for the shape of the answer — bar, line, choropleth, stat — and renders it automatically. No dragging fields onto a canvas.",
        metric: "10+ chart types, auto-selected",
      },
      {
        icon: "BrainCircuit",
        title: "It's an autonomous analyst",
        body: "Ask a broad question and Sema plans the sub-queries, assembles the findings, writes the narrative from real numbers, and builds a shareable deck — PPTX, PDF, or web.",
        metric: "Question → full analysis & deck",
      },
      {
        icon: "ShieldCheck",
        title: "Governance is built in",
        body: "Row-level access, column masking, refusals, and Ed25519-signed evidence are on by default — not a plugin you remember to add later.",
        metric: "Signed & audited, every answer",
      },
      {
        icon: "Timer",
        title: "It saves time & cost",
        body: "Collapse the Slack → BI → spreadsheet → SQL round-trip into one prompt. Audits that took weeks take an afternoon; analyst hours go back to analysis.",
        metric: "Weeks of audit prep → hours",
      },
    ],
  },
  pipeline: {
    heading: "What happens between your question and the answer",
    nodes: [
      { id: "q", label: "Question", sub: "Plain English", icon: "MessageSquare" },
      { id: "graph", label: "Semantic graph", sub: "Meaning + relationships", icon: "Network" },
      { id: "plan", label: "Governed planner", sub: "RBAC + policy at plan time", icon: "ShieldCheck" },
      { id: "sql", label: "Sandboxed SQL", sub: "Static-analysed, read-only", icon: "TerminalSquare" },
      { id: "viz", label: "Auto-visualization", sub: "The right chart, chosen", icon: "BarChart3" },
      { id: "evidence", label: "Signed evidence", sub: "Ed25519 · audit-ready", icon: "FileCheck2" },
    ],
  },
  stack: {
    heading: "The stack you'd otherwise have to build",
    note: "Each layer is months of specialised engineering. Together they're the reason an answer is trustworthy — and the reason this is hard to replicate.",
    layers: [
      { name: "Semantic knowledge graph", detail: "Typed entities, verified joins, first-class metrics — a control plane, not a wiki." },
      { name: "Grounded planning + repair", detail: "LLM planning constrained to approved entities, with one safe repair-retry on error." },
      { name: "Policy & RBAC engine", detail: "Role-aware refusals and column masking enforced before any SQL executes." },
      { name: "Sandboxed execution", detail: "Ephemeral, network-isolated runners; generated SQL statically analysed first." },
      { name: "Auto-visualization", detail: "Result-shape-aware charting and dashboard assembly." },
      { name: "Cryptographic evidence + audit", detail: "Ed25519-signed answer bundles and an append-only, exportable audit log." },
    ],
  },
};

export const MEDIA_KIT = {
  eyebrow: "press & media",
  title: "Media kit.",
  subtitle:
    "Everything you need to write about Sema — logos, brand colors, a company one-pager, and product screenshots. Grab an individual asset or the full kit.",
  boilerplate:
    "Sema is the semantic layer for enterprise AI — governed, explainable text-to-SQL that lets teams ask business questions in plain English and get audit-ready answers with cryptographically signed evidence.",
  pressEmail: "press@datafrontier.co",
  items: [
    { label: "Logo pack", desc: "Mark + lockup · light & dark · PNG/WebP", href: "/media-kit/sema-media-kit.zip", icon: "ImageDown", kind: "primary" },
    { label: "Brand colors", desc: "Palette + hex codes", href: "/media-kit/sema-brand-colors.txt", icon: "Palette" },
    { label: "One-pager", desc: "Company overview · PDF", href: "/media-kit/sema-one-pager.pdf", icon: "FileText" },
    { label: "Dashboard screenshot", desc: "Product UI · JPG", href: "/media-kit/sema-screenshot-dashboard.jpg", icon: "MonitorPlay" },
    { label: "Evidence pack screenshot", desc: "Audit view · JPG", href: "/media-kit/sema-screenshot-evidence.jpg", icon: "ShieldCheck" },
    { label: "Full media kit", desc: "Everything above · ZIP", href: "/media-kit/sema-media-kit.zip", icon: "Package", kind: "zip" },
  ],
};
